import React, { createContext, useState, useEffect, useContext } from 'react';
import { Capacitor } from '@capacitor/core';
import {
  onAuthStateChanged,
  signInWithPopup,
  signInWithCredential,
  GoogleAuthProvider,
  signOut,
  setPersistence,
  browserLocalPersistence,
  indexedDBLocalPersistence,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  updateProfile,
} from 'firebase/auth';
import { doc, getDoc, setDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db, googleProvider } from '../firebase';

// ✅ Detecta si estamos en app nativa (Capacitor)
const getIsNative = () => Capacitor.isNativePlatform();

const AuthContext = createContext();
export function useAuth() { return useContext(AuthContext); }

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);

  // ── LOGIN ADMIN con Google ─────────────────────────────
  async function loginAdmin() {
  console.log('🔍 isNative:', getIsNative());
  console.log('🔍 window.Capacitor:', JSON.stringify(window.Capacitor));
    try {
      await setPersistence(auth, indexedDBLocalPersistence);
    } catch(e) {
      await setPersistence(auth, browserLocalPersistence);
    }

    if (getIsNative()) {
      // ✅ Usa @capacitor-firebase/authentication (compatible con Capacitor 8)
      const { FirebaseAuthentication } = await import('@capacitor-firebase/authentication');
      const result = await FirebaseAuthentication.signInWithGoogle();
      if (!result?.credential?.idToken) throw new Error('No se obtuvo token de Google');
      const credential = GoogleAuthProvider.credential(result.credential.idToken);
      const fbResult = await signInWithCredential(auth, credential);
      await _saveAdminUser(fbResult.user);
      return fbResult.user;
    }

    // Web: usa popup normal
    const result = await signInWithPopup(auth, googleProvider);
    await _saveAdminUser(result.user);
    return result.user;
  }

  async function _saveAdminUser(user) {
    const userRef = doc(db, 'users', user.uid);
    const userSnap = await getDoc(userRef);
    if (!userSnap.exists()) {
      await setDoc(userRef, {
        uid: user.uid,
        name: user.displayName,
        email: user.email,
        avatar: user.photoURL,
        role: 'admin',
        groupIds: [],
        createdAt: serverTimestamp()
      });
    }
  }

  // ── REGISTRO con link (primera vez) ───────────────────
  async function registerWithLink(linkId, name, email, password) {
    const linkRef = doc(db, 'inviteLinks', linkId);
    const linkSnap = await getDoc(linkRef);
    if (!linkSnap.exists()) throw new Error('Link inválido o no existe.');
    const linkData = linkSnap.data();

    if (linkData.used && linkData.usedByEmail && linkData.usedByEmail !== email.toLowerCase()) {
      throw new Error('Este link ya fue usado por otra persona. Pide un nuevo link al admin.');
    }

    const groupSnap = await getDoc(doc(db, 'groups', linkData.groupId));
    if (!groupSnap.exists()) throw new Error('El grupo ya no existe.');

    try {
      await setPersistence(auth, indexedDBLocalPersistence);
    } catch(e) {
      await setPersistence(auth, browserLocalPersistence);
    }

    let user;
    try {
      const cred = await createUserWithEmailAndPassword(auth, email.toLowerCase(), password);
      user = cred.user;
    } catch (err) {
      if (err.code === 'auth/email-already-in-use') {
        try {
          const cred = await signInWithEmailAndPassword(auth, email.toLowerCase(), password);
          user = cred.user;
        } catch {
          throw new Error('Ya tienes cuenta con ese email pero la contraseña no es correcta.');
        }
      } else if (err.code === 'auth/weak-password') {
        throw new Error('La contraseña debe tener al menos 6 caracteres.');
      } else {
        throw new Error('Error creando la cuenta. Intenta de nuevo.');
      }
    }

    const existingSnap = await getDoc(doc(db, 'users', user.uid));
    const existing = existingSnap.exists() ? existingSnap.data() : null;
    const existingGroupIds = existing?.groupIds || (existing?.groupId ? [existing.groupId] : []);
    const newGroupIds = existingGroupIds.includes(linkData.groupId)
      ? existingGroupIds
      : [...existingGroupIds, linkData.groupId];

    await setDoc(doc(db, 'users', user.uid), {
      uid: user.uid,
      name: name.trim() || linkData.participantName || 'Participante',
      email: email.toLowerCase(),
      role: 'participant',
      groupId: linkData.groupId,
      groupIds: newGroupIds,
      createdAt: existing?.createdAt || serverTimestamp(),
      updatedAt: serverTimestamp()
    });

    const memberRef = doc(db, 'groups', linkData.groupId, 'members', user.uid);
    const memberSnap = await getDoc(memberRef);
    if (!memberSnap.exists()) {
      await setDoc(memberRef, {
        userId: user.uid,
        name: name.trim() || linkData.participantName || 'Participante',
        email: email.toLowerCase(),
        joinedAt: serverTimestamp(),
        inviteLinkId: linkId,
        totalPoints: 0,
        exactPredictions: 0,
        correctPredictions: 0
      });
      await updateDoc(doc(db, 'groups', linkData.groupId), {
        currentMembers: (groupSnap.data().currentMembers || 0) + 1
      });
    }

    try {
      await updateDoc(linkRef, {
        used: true,
        usedBy: user.uid,
        usedByEmail: email.toLowerCase(),
        usedAt: serverTimestamp()
      });
    } catch(e) {
      console.warn('No se pudo marcar el link como usado:', e);
    }

    return user;
  }

  // ── REGISTRO NUEVO USUARIO (sin link) ─────────────────
  async function registerUser(name, email, password) {
    try {
      await setPersistence(auth, indexedDBLocalPersistence);
    } catch(e) {
      await setPersistence(auth, browserLocalPersistence);
    }
    const cred = await createUserWithEmailAndPassword(auth, email.toLowerCase(), password);
    const user = cred.user;
    await updateProfile(user, { displayName: name.trim() });
    await setDoc(doc(db, 'users', user.uid), {
      uid: user.uid,
      name: name.trim(),
      email: email.toLowerCase(),
      role: 'admin',
      groupIds: [],
      createdAt: serverTimestamp(),
    });
    return user;
  }

  // ── LOGIN CON GOOGLE (participantes) ──────────────────
  async function loginWithGoogle() {
    try {
      await setPersistence(auth, indexedDBLocalPersistence);
    } catch(e) {
      await setPersistence(auth, browserLocalPersistence);
    }

    let user;
    if (getIsNative()) {
      // ✅ Usa @capacitor-firebase/authentication (compatible con Capacitor 8)
      const { FirebaseAuthentication } = await import('@capacitor-firebase/authentication');
      let result;
      try {
        result = await FirebaseAuthentication.signInWithGoogle();
      } catch(e) {
        const msg = e?.message || e?.errorMessage || JSON.stringify(e) || 'Error desconocido';
        throw new Error('Google Sign-In cancelado o falló: ' + msg);
      }

      if (!result?.credential?.idToken) {
        throw new Error('No se obtuvo el token de Google. Intenta de nuevo.');
      }

      const credential = GoogleAuthProvider.credential(result.credential.idToken);
      try {
        const fbResult = await signInWithCredential(auth, credential);
        user = fbResult.user;
      } catch(e) {
        throw new Error('Error Firebase Auth: ' + (e?.code || e?.message));
      }
    } else {
      // Web: usa popup normal
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);
      user = result.user;
    }

    try {
      const userRef = doc(db, 'users', user.uid);
      const snap = await getDoc(userRef);
      if (!snap.exists()) {
        await setDoc(userRef, {
          uid: user.uid,
          name: user.displayName || '',
          email: user.email,
          avatar: user.photoURL || '',
          role: 'admin',
          groupIds: [],
          createdAt: serverTimestamp(),
        });
      }
    } catch(e) {
      console.warn('Firestore save warning:', e);
    }

    return user;
  }

  // ── LOGIN PARTICIPANTE ya registrado ──────────────────
  async function loginParticipant(email, password) {
    try {
      await setPersistence(auth, indexedDBLocalPersistence);
    } catch(e) {
      await setPersistence(auth, browserLocalPersistence);
    }
    try {
      const cred = await signInWithEmailAndPassword(auth, email.toLowerCase(), password);
      return cred.user;
    } catch (err) {
      if (err.code === 'auth/user-not-found' || err.code === 'auth/wrong-password' || err.code === 'auth/invalid-credential') {
        throw new Error('Email o contraseña incorrectos.');
      }
      throw new Error('Error al iniciar sesión. Intenta de nuevo.');
    }
  }

  async function refreshUserData() {
    if (!auth.currentUser) return;
    const snap = await getDoc(doc(db, 'users', auth.currentUser.uid));
    if (snap.exists()) setUserData(snap.data());
  }

  function logout() { return signOut(auth); }

  // ── ESCUCHAR CAMBIOS DE AUTH ───────────────────────────
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      if (user) {
        const snap = await getDoc(doc(db, 'users', user.uid));
        if (snap.exists()) {
          setUserData(snap.data());
        } else {
          setUserData(null);
        }
      } else {
        setUserData(null);
      }
      setLoading(false);
    });
    return unsubscribe;
  }, []);

  return (
    <AuthContext.Provider value={{
      currentUser, userData, loading,
      loginAdmin, loginParticipant, registerWithLink, registerUser, loginWithGoogle, logout, refreshUserData
    }}>
      {!loading && children}
    </AuthContext.Provider>
  );
}